import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";
import type { PageResponse } from "../types";

type Props<T> = {
  page: PageResponse<T>;
  onPageChange: (page: number) => void;
  onSizeChange?: (size: number) => void;
  sizes?: number[];
};

function buildPageNumbers(current: number, total: number): (number | "…")[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i);
  const always = new Set([0, 1, current - 1, current, current + 1, total - 2, total - 1]);
  const inRange = [...always].filter((p) => p >= 0 && p < total).sort((a, b) => a - b);
  const pages: (number | "…")[] = [];
  let prev = -1;
  for (const p of inRange) {
    if (prev !== -1 && p - prev > 1) pages.push("…");
    pages.push(p);
    prev = p;
  }
  return pages;
}

export function LeavePagination<T>({
  page,
  onPageChange,
  onSizeChange,
  sizes = [10, 20, 50, 100],
}: Props<T>) {
  const currentPage = page.page;
  const totalPages = Math.max(1, page.totalPages || 1);
  const isFirst = currentPage <= 0;
  const isLast = currentPage >= totalPages - 1;
  const pageNumbers = buildPageNumbers(currentPage, totalPages);

  const navBtn =
    "flex h-8 w-8 items-center justify-center rounded-lg text-sm font-semibold text-slate-500 transition-colors hover:bg-slate-100 disabled:pointer-events-none disabled:opacity-30";

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border bg-white px-4 py-3 shadow-sm">
      <div className="flex items-center gap-3">
        <span className="text-sm text-slate-400">
          <span className="font-semibold text-slate-700">{page.totalElements ?? 0}</span> result{page.totalElements !== 1 ? "s" : ""}
        </span>
        {onSizeChange && (
          <select
            className="rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
            value={page.size}
            onChange={(e) => onSizeChange(Number(e.target.value))}
          >
            {sizes.map((s) => (
              <option key={s} value={s}>{s} / page</option>
            ))}
          </select>
        )}
      </div>

      <div className="flex items-center gap-0.5">
        <button type="button" disabled={isFirst} onClick={() => onPageChange(0)} className={navBtn}>
          <ChevronsLeft className="h-4 w-4" />
        </button>
        <button type="button" disabled={isFirst} onClick={() => onPageChange(currentPage - 1)} className={navBtn}>
          <ChevronLeft className="h-4 w-4" />
        </button>

        {pageNumbers.map((p, i) =>
          p === "…" ? (
            <span key={`e-${i}`} className="flex h-8 w-8 items-center justify-center text-xs text-slate-300">…</span>
          ) : (
            <button
              key={p}
              type="button"
              onClick={() => onPageChange(p)}
              className={`flex h-8 w-8 items-center justify-center rounded-lg text-sm font-semibold transition-colors ${
                p === currentPage
                  ? "bg-emerald-600 text-white shadow-sm"
                  : "text-slate-600 hover:bg-slate-100"
              }`}
            >
              {p + 1}
            </button>
          ),
        )}

        <button type="button" disabled={isLast} onClick={() => onPageChange(currentPage + 1)} className={navBtn}>
          <ChevronRight className="h-4 w-4" />
        </button>
        <button type="button" disabled={isLast} onClick={() => onPageChange(totalPages - 1)} className={navBtn}>
          <ChevronsRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
